import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const JSPrivateFields = () => {
    const hardPrivacyCode = `// --- ES2022 Hard Privacy (#) ---
class SecureVault {
    // 1. Declaration is mandatory
    #secretKey;
    #accessCount = 0;

    constructor(initialKey) {
        this.#secretKey = initialKey;
    }

    // 2. Private Method
    #incrementAccess() {
        this.#accessCount++;
        console.log(\`Access #\${this.#accessCount}\`);
    }

    unlock(inputKey) {
        if (inputKey === this.#secretKey) {
            this.#incrementAccess();
            return 'Sensors Activated';
        }
        return 'Access Denied';
    }
}

const vault = new SecureVault('BLUE-BIRD');
console.log(vault.unlock('BLUE-BIRD'));
// console.log(vault.#secretKey); //  Uncaught SyntaxError`;

    const weakMapPrivacyCode = `// --- The WeakMap Pattern (Legacy "Hard" Privacy) ---
const _internalState = new WeakMap();

class UserAccount {
    constructor(userId) {
        _internalState.set(this, {
            userId: userId,
            loginTime: Date.now()
        });
    }

    getDetails() {
        const state = _internalState.get(this);
        return \`User: \${state.userId} logged at \${state.loginTime}\`;
    }
}

// Memory Efficient: When the UserAccount instance is garbage 
// collected, the WeakMap entry is also removed automatically.`;

    const stateMachineCode = `// --- Private State Machine Pattern ---
class Transaction {
    #state = 'PENDING'; // Truly encapsulated state

    #transitions = {
        'PENDING': ['AUTHORIZED', 'CANCELLED'],
        'AUTHORIZED': ['CAPTURED', 'REFUNDED'],
    };

    process(nextState) {
        const allowed = this.#transitions[this.#state];
        if (allowed?.includes(nextState)) {
            this.#state = nextState;
            console.log(\`Status: \${this.#state}\`);
        } else {
            throw new Error(\`Invalid move: \${this.#state} -&gt; \${nextState}\`);
        }
    }
}

const tx = new Transaction();
tx.process('AUTHORIZED');
// tx.#state = 'CAPTURED'; //  Cannot bypass the process() logic`;

    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Encapsulation</div>
                <h1>The Final Frontier: Private Fields</h1>
                <p className="hero-lead">
                    Before ES2022, JavaScript lacked true encapsulation, relying
                    on naming conventions like total <code>_underscores</code>.
                    With <strong>Private Fields</strong>, JS now provides
                    language-level "Hard Privacy" that cannot be bypassed by
                    external code.
                </p>
            </div>

            <div className="content-section">
                <h2>Hard Privacy with the Hash (#) Syntax</h2>
                <p>
                    Private fields, prefixed with <code>#</code>, are not merely
                    hidden; they are <strong>unreachable</strong> from outside
                    the class body. This ensures that internal implementation
                    details can change without breaking consumer codethe
                    fundamental promise of Object-Oriented Programming.
                </p>
                <CodeBlock language="javascript" code={hardPrivacyCode} />
            </div>

            <div className="content-section">
                <h2>Legacy Privacy: The WeakMap Pattern</h2>
                <p>
                    Before the <code>#</code> syntax, developers used
                    <code>WeakMap</code> to store private data. This pattern
                    associates private state with a specific object instance
                    without preventing garbage collection, ensuring memory
                    efficiency even for large sets of objects.
                </p>
                <CodeBlock language="javascript" code={weakMapPrivacyCode} />
            </div>

            <div className="content-section">
                <h2>Encapsulated State Machines</h2>
                <p>
                    The most powerful use case for private fields is
                    <strong>Internal State Management</strong>. By making the status
                    field private, you force all updates to go through a valid
                    transition method, preventing objects from entering
                    impossible states.
                </p>
                <CodeBlock language="javascript" code={stateMachineCode} />
                <ProTip><strong> Design Tip:</strong> Always default to private.
                    Only make a field public when you are certain it needs to be
                    part of the object's external API. Minimal surface area
                    equals fewer bugs.</ProTip>
            </div>

            <KeyFacts><ul>
                    <li><strong>Hard Privacy:</strong> Use <code>#</code> for data that should NEVER be touched by external consumers.</li>
                    <li><strong>Mandatory Declaration:</strong> Unlike public fields, private fields MUST be declared in the class body.</li>
                    <li> <strong>Inheritance:</strong> Remember that private fields are NOT visible to subclasses (even <code>super</code> cannot see them).</li>
                    <li><strong>Performance:</strong> Modern engines (V8) can optimize private field access because the list of properties is fixed at compile-time.</li>
                    <li><strong>Refactoring:</strong> Use privacy to hide the "messy" internals of your API, keeping the public interface clean.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSPrivateFields;



